'use client';
import React from 'react';
import { BarChart3, CheckCircle } from 'lucide-react';
import type { MoveRecord } from './MatchRoomContent';

interface Props {
  scores: { p1: number; p2: number };
  round: number;
  maxRounds: number;
  moveHistory: MoveRecord[];
}

export default function MatchScorePanel({ scores, round, maxRounds, moveHistory }: Props) {
  const p1Moves = moveHistory.filter(m => m.player === 'p1').length;
  const p2Moves = moveHistory.filter(m => m.player === 'p2').length;
  const p1Valid = moveHistory.filter(m => m.player === 'p1' && m.valid).length;
  const p2Valid = moveHistory.filter(m => m.player === 'p2' && m.valid).length;

  const rounds = Array.from({ length: maxRounds }, (_, i) => {
    const roundMoves = moveHistory.filter(m => m.roundScore !== undefined);
    return { idx: i + 1, done: i + 1 < round };
  });

  return (
    <div className="border-b px-3 py-3" style={{ borderColor: 'var(--border)' }}>
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 size={14} style={{ color: '#A78BFA' }} />
        <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Score Sync</span>
        <span className="ml-auto flex items-center gap-1 text-xs" style={{ color: '#10B981' }}>
          <CheckCircle size={11} /> Live
        </span>
      </div>

      {/* Round indicators */}
      <div className="flex items-center gap-2 mb-3">
        {Array.from({ length: maxRounds }, (_, i) => {
          const roundNum = i + 1;
          const done = roundNum < round;
          const current = roundNum === round;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: done ? '100%' : current ? '50%' : '0%', background: done ? '#10B981' : '#7C3AED' }} />
              </div>
              <span className="text-xs" style={{ color: current ? '#A78BFA' : done ? '#10B981' : 'var(--muted-foreground)', fontWeight: current ? 700 : 400 }}>
                R{roundNum}
              </span>
            </div>
          );
        })}
      </div>

      {/* Score comparison */}
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <div>
          <p className="text-2xl font-black" style={{ color: '#A78BFA' }}>{scores.p1}</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>You</p>
        </div>
        <div className="flex items-center justify-center">
          <span className="text-lg font-bold" style={{ color: 'var(--muted-foreground)' }}>vs</span>
        </div>
        <div>
          <p className="text-2xl font-black" style={{ color: '#F59E0B' }}>{scores.p2}</p>
          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Opp</p>
        </div>
      </div>

      {/* Stats */}
      <div className="space-y-1.5">
        <StatRow label="Moves" p1={p1Moves} p2={p2Moves} />
        <StatRow label="Valid" p1={p1Valid} p2={p2Valid} />
        <StatRow label="Accuracy" p1={p1Moves > 0 ? Math.round((p1Valid / p1Moves) * 100) : 100} p2={p2Moves > 0 ? Math.round((p2Valid / p2Moves) * 100) : 100} suffix="%" />
      </div>
    </div>
  );
}

function StatRow({ label, p1, p2, suffix = '' }: { label: string; p1: number; p2: number; suffix?: string }) {
  const total = p1 + p2 || 1;
  const p1Pct = (p1 / total) * 100;
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5">
        <span style={{ color: '#A78BFA' }}>{p1}{suffix}</span>
        <span style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>{label}</span>
        <span style={{ color: '#F59E0B' }}>{p2}{suffix}</span>
      </div>
      <div className="flex h-1 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
        <div style={{ width: `${p1Pct}%`, background: '#7C3AED', transition: 'width 0.5s' }} />
        <div style={{ flex: 1, background: '#F59E0B' }} />
      </div>
    </div>
  );
}
